import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppStore } from '../../App';
import { Conversation, Message, Participant } from './types';
import { conversationApi } from './api';
import { getAdapter } from './adapters';
import type { AdapterContext } from './adapters/types';
import ThreadShell from './components/ThreadShell';
import PeerInfoModal from './components/PeerInfoModal';
import { MessagesProvider } from './MessagesContext';

const ConversationView: React.FC = () => {
 const { cid } = useParams<{ cid: string }>();
 const navigate = useNavigate();
 const user = useAppStore(s => s.user);
 const refreshUnreadInbox = useAppStore(s => s.refreshUnreadInbox);
 const [conv, setConv] = useState<Conversation | null>(null);
 const [messages, setMessages] = useState<Message[]>([]);
 const [participants, setParticipants] = useState<Participant[]>([]);
 const [error, setError] = useState<string | null>(null);
 const [busy, setBusy] = useState(false);
 const [selectedPeer, setSelectedPeer] = useState<Participant | null>(null);

 const reload = useCallback(async () => {
  if (!cid) return;
  try {
   const r = await fetch(`/messages/${cid}`, { credentials: 'include' });
   const j = await r.json();
   if (!j.success) { setError(j.error || '조회 실패'); return; }
   setConv(j.data.conversation);
   setMessages(j.data.messages || []);
   setParticipants(j.data.participants || []);
   await fetch(`/messages/${cid}/read`, { method: 'PATCH', credentials: 'include' });
   // 읽음 처리 후 좌측 메뉴 카운트 갱신
   refreshUnreadInbox();
  } catch (e: any) {
   setError(e.message || '조회 실패');
  }
 }, [cid, refreshUnreadInbox]);

 useEffect(() => { reload(); }, [reload]);

 if (error) return <div className="bg-red-50 text-red-700 p-3 text-sm">{error}</div>;
 if (!conv) {
  return (
   <div className="text-gray-500 py-8 text-center text-sm">
    <div className="inline-block animate-spin h-8 w-8 rounded-full border-4 border-gray-200 border-t-blue-500"></div>
    <div className="mt-2">로딩 중...</div>
   </div>
  );
 }
 if (!user) return <div className="text-gray-500 py-8 text-center text-sm">사용자 정보 없음</div>;

 const adapter = getAdapter(conv.kind);
 const ctx: AdapterContext = {
  conv,
  messages,
  participants,
  viewer: { peer_hash: user.moPeerHash },
  api: conversationApi,
  onChange: reload,
  busy,
  setBusy,
 };

 return (
  <MessagesProvider value={{
   participants,
   viewerPeerHash: user.moPeerHash,
   onPeerClick: setSelectedPeer,
   onFamilyClick: (hash) => navigate(`/mofamily/${hash}`),
  }}>
   <ThreadShell
    conv={conv}
    participants={participants}
    onBack={() => navigate(-1)}
    actionPanel={adapter.renderActionPanel(ctx)}
   >
    {/* 시간 역순(최신이 위) — 위의 액션 폼이 가장 최근 흐름에 바로 이어지게. */}
    {[...messages].reverse().map(m => adapter.renderMessage(m, ctx))}
   </ThreadShell>
   {selectedPeer && (
    <PeerInfoModal
     peerHash={selectedPeer.peer_hash || selectedPeer.acting_peer_hash || ''}
     name={selectedPeer.peer_name}
     email={selectedPeer.peer_email}
     profileImage={selectedPeer.peer_profile_image}
     onClose={() => setSelectedPeer(null)}
    />
   )}
  </MessagesProvider>
 );
};

export default ConversationView;
